import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';

import { useAuth } from '../auth';
import { CompactTable } from '../components/CompactTable';
import { GuestInvitationPanel } from '../components/GuestInvitationPanel';
import { EventPhoneReservationsPanel } from '../components/EventPhoneReservationsPanel';
import {
  getPortalEventParticipants,
  PortalApiError,
  removePortalEventParticipant,
  setPortalEventParticipant,
} from '../services/portalApi';
import type {
  EventParticipantsResponse,
  PortalEventRole,
} from '../types/api';

export function EventDetailsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { getAccessToken } = useAuth();
  const [details, setDetails] = useState<EventParticipantsResponse | null>(null);
  const [selectedLoginId, setSelectedLoginId] = useState('');
  const [selectedRole, setSelectedRole] = useState<PortalEventRole>('Caller');
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  const loadDetails = useCallback(async (): Promise<void> => {
    if (!eventId) return;
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      setDetails(await getPortalEventParticipants(token, eventId));
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }, [eventId, getAccessToken]);

  useEffect(() => {
    if (!eventId) return;
    const requestedEventId: string = eventId;
    let cancelled = false;

    async function loadInitialDetails(): Promise<void> {
      try {
        const token = await getAccessToken();
        const response = await getPortalEventParticipants(token, requestedEventId);
        if (!cancelled) setDetails(response);
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    }

    void loadInitialDetails();
    return () => { cancelled = true; };
  }, [eventId, getAccessToken]);

  async function addParticipant(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!eventId || !selectedLoginId) return;

    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await setPortalEventParticipant(token, eventId, {
        loginId: selectedLoginId,
        role: selectedRole,
      });
      setSelectedLoginId('');
      await loadDetails();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  async function changeRole(loginId: string, role: PortalEventRole): Promise<void> {
    if (!eventId) return;
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await setPortalEventParticipant(token, eventId, { loginId, role });
      await loadDetails();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  async function removeParticipant(loginId: string): Promise<void> {
    if (!eventId || !window.confirm(`Remove ${loginId} from this event?`)) return;
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await removePortalEventParticipant(token, eventId, loginId);
      await loadDetails();
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  if (!eventId) {
    return <p className="api-message">Event ID is missing.</p>;
  }

  return (
    <section className="events-page">
      <Link className="back-link" to="/events">← Back to events</Link>

      {details ? (
        <>
          <div className="section-heading event-details-heading">
            <div>
              <p className="eyebrow">Event</p>
              <h1>{details.Event.Name}</h1>
              <p className="hero-description">
                {details.Event.Description ?? 'Manage reserved phones, participants and event roles.'}
              </p>
            </div>
            {details.Access.Role ? (
              <span className="role-badge">{formatRole(details.Access.Role)}</span>
            ) : null}
          </div>

          {details.Access.CanEditEvent || details.Access.Role === 'CallLead' ? (
            <EventPhoneReservationsPanel eventId={eventId} />
          ) : null}

          {details.Access.CanManageParticipants ? (
            <form className="participant-form" onSubmit={(event) => void addParticipant(event)}>
              <label>
                Portal user
                <select required value={selectedLoginId}
                  onChange={(event) => setSelectedLoginId(event.target.value)}>
                  <option value="">Select an existing user</option>
                  {details.Candidates.map((candidate) => (
                    <option key={candidate.LoginId} value={candidate.LoginId}>
                      {formatName(candidate.FirstName, candidate.LastName, candidate.Email)}
                      {candidate.IdentityType === 'Guest' ? ' (External)' : ''}
                      {candidate.Organization ? ` — ${candidate.Organization}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Event role
                <select value={selectedRole}
                  onChange={(event) => setSelectedRole(event.target.value as PortalEventRole)}>
                  {details.Access.AssignableRoles.map((role) => (
                    <option key={role} value={role}>{formatRole(role)}</option>
                  ))}
                </select>
              </label>
              <button className="primary-button" disabled={isBusy || !selectedLoginId} type="submit">
                Add participant
              </button>
            </form>
          ) : null}

          {details.Access.CanManageParticipants ? (
            <GuestInvitationPanel
              events={[details.Event]}
              assignableRoles={details.Access.AssignableRoles}
            />
          ) : null}

          <div className="participant-list-section">
            <h2>Participants</h2>
            {details.Participants.length === 0 ? (
              <div className="event-empty-state">
                <p>No participants have been added.</p>
              </div>
            ) : (
              <CompactTable
                label="Event participants"
                headers={[
                  { key: 'name', label: 'Name' },
                  { key: 'email', label: 'Email' },
                  { key: 'organization', label: 'Organization' },
                  { key: 'role', label: 'Role' },
                  { key: 'actions', label: '' },
                ]}
              >
                {details.Participants.map((participant) => {
                  const canChange = details.Access.AssignableRoles.includes(participant.Role);
                  return (
                    <tr key={participant.LoginId}>
                      <td>
                        {formatName(participant.FirstName, participant.LastName, participant.Email)}
                        {participant.IdentityType === 'Guest' ? (
                          <span className="external-user-badge">External</span>
                        ) : null}
                      </td>
                      <td>{participant.Email}</td>
                      <td>{participant.Organization ?? '—'}</td>
                      <td>
                        {canChange ? (
                          <select className="table-select" value={participant.Role}
                            disabled={isBusy}
                            onChange={(event) => void changeRole(
                              participant.LoginId,
                              event.target.value as PortalEventRole,
                            )}>
                            {details.Access.AssignableRoles.map((role) => (
                              <option key={role} value={role}>{formatRole(role)}</option>
                            ))}
                          </select>
                        ) : formatRole(participant.Role)}
                      </td>
                      <td className="compact-table-actions">
                        {canChange ? (
                          <button className="table-action-button table-action-danger"
                            type="button" disabled={isBusy}
                            onClick={() => void removeParticipant(participant.LoginId)}>
                            Remove
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </CompactTable>
            )}
          </div>
        </>
      ) : null}

      {isBusy && !details ? <p>Loading event...</p> : null}
      {message ? <p className="api-message" role="alert">{message}</p> : null}
    </section>
  );
}

function formatName(firstName: string, lastName: string, fallback: string): string {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || fallback;
}

function formatRole(role: PortalEventRole): string {
  if (role === 'EventManager') return 'Event Manager';
  if (role === 'CallLead') return 'Call Lead';
  return 'Caller';
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError) return error.message;
  return 'The event participant request could not be completed.';
}
